import { callApi } from "utils/api";
import { resetSocketHandlers } from 'utils/socket/chat/handlers'
import { clearAllTabScopedDrafts } from 'utils/tabDraftStorage'

export const setUniqueSessionToken = (type, token, nonUnique = false) => {
    const types = [
        "@token",
        "@super_admin_token",
        "@supplier_token",
        "@passenger_token",
        "@driver_token",
        "@client_token",
        "@employee_code",
    ];

    // Detect if this is an impersonated session
    // Check storage AND URL (fixes race condition: child useEffects run before parent)
    const impersonatedFromUrl = typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).get("impersonated") === "1";
    const isImpersonated =
        localStorage.getItem("@impersonated") === "1" ||
        sessionStorage.getItem("@impersonated") === "1" ||
        impersonatedFromUrl;

    // DEBUG: Remove after testing
    console.log(`[setUniqueSessionToken] type=${type}, nonUnique=${nonUnique}, isImpersonated=${isImpersonated}, impersonatedFromUrl=${impersonatedFromUrl}, URL=${window.location.search}, storage=${isImpersonated ? 'sessionStorage' : 'localStorage'}`);

    // Ensure the flag is set for subsequent calls
    if (isImpersonated && sessionStorage.getItem("@impersonated") !== "1") {
        sessionStorage.setItem("@impersonated", "1");
    }

    // Impersonated sessions store tokens in sessionStorage (per-tab, auto-cleared on close)
    const storage = isImpersonated ? sessionStorage : localStorage;

    types.forEach((tp) => {
        if (!nonUnique) {
            if (tp !== type) {
                localStorage.removeItem(tp);
                if (isImpersonated) sessionStorage.removeItem(tp);
            } else {
                storage.setItem(type, token);
            }
            return;
        }

        if (tp === type) {
            storage.setItem(type, token);
            return;
        }

        if (tp === "@super_admin_token") {
            return;
        }
    });

    // [IMPERSONATED SESSION BRIDGE]
    // When storing a token in an impersonated session, publish the bridge to localStorage.
    // This ensures any new tab opened anyhow (Ctrl+click, right-click, window.open) can
    // pick up the impersonation and authenticate itself — sessionStorage is per-tab only.
    if (isImpersonated && type !== '@super_admin_token') {
        try {
            const bridge = {
                token,
                role: (() => {
                    const keyRoleMap = {
                        '@client_token': 'client',
                        '@driver_token': 'driver',
                        '@passenger_token': 'passenger',
                        '@supplier_token': 'supplier',
                        '@token': 'user',
                    };
                    return keyRoleMap[type] || null;
                })(),
                ts: Date.now(),
            };
            if (bridge.role) {
                localStorage.setItem('@imp_bridge', JSON.stringify(bridge));
                console.log(`[ImpersonationBridge] Bridge updated for role: ${bridge.role}`);
            }
        } catch (e) {
            console.warn('[ImpersonationBridge] Failed to write bridge:', e);
        }
    }
};

export const preLogin = (username) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/preLogin`, {
                method: "POST",
                body: JSON.stringify({ username }),
                showDefaultMessage: false,
            });
            return res.body;
        } catch (e) {
            console.error(e);
            throw e;
        }
    };
};

export const login = ({ username, password }) => {
    console.log("Login");
    return async (dispatch, getState) => {
        let res = { body: "" };
        try {
            res = await callApi(`/app/login`, {
                method: "POST",
                body: JSON.stringify({
                    username,
                    password,
                }),
            });

            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@token", token);

                    dispatch({
                        type: "LOGIN",
                        payload: res.body,
                    });
                    return res.body;
                } catch (e) {
                    console.error(e);
                    throw e;
                }
            } else {
                console.group("[APP | store/actions/app.js]");
                console.log("Status: ", res.status);
                const errorMsg = res.body?.error || "Error desconocido";
                console.error("Login failed:", errorMsg);
                console.groupEnd();
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[APP | store/actions/app.js]", error);
            throw error;
        }
    };
};

export const loginWithToken = (options) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/token`, {
                method: "POST",
                body: JSON.stringify({
                    token: options.token,
                }),
                maxRetries: 2, // Habilitar reintentos SOLO para login automático
                retryDelay: 2000, // 2 segundos entre reintentos
            });

            if (res.status === 200) {
                try {
                    // console.log("Login with Token");
                    const token = res.body.token;
                    setUniqueSessionToken("@token", token, options?.nonUnique);
                    dispatch({
                        type: "LOGIN",
                        payload: res.body,
                    });
                    let state = getState();
                    let list = [];
                    const res_visits = await callApi(`/user/last_visits`, {
                        method: "GET",
                        headers: {
                            Authorization: res.body.token,
                        },
                        params: {
                            user_id: res.body.id,
                            sys_company_id: res.body.sys_company_id,
                        },
                    });

                    if (res_visits.body.response.length > 0) {
                        dispatch({
                            type: "SET_LAST_VISIT_LIST",
                            payload: res_visits.body.response,
                        });
                    } else {
                        dispatch({
                            type: "INIT_LAST_VISITS_LIST",
                        });
                    }
                    return res.body;
                } catch (e) {
                    console.error("[loginWithToken] Error processing token:", e);
                    throw new Error("Error al procesar el token de login");
                }
            } else {
                const errorMsg =
                    res.body?.error || res.body?.message || "Token inválido";
                console.error("[loginWithToken] Login failed:", errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[loginWithToken] Exception:", error);
            if (error?.body) throw error;
            throw new Error(error.message || "Token no válido");
        }
    };
};

export const actionClick = (data) => {
    return async (dispatch, getState) => {
        let state = getState();
        let list = state?.app?.user?.last_visits;

        if (!list) {
            list = [];
        }
        if (!list.some((item) => item.name == data.name)) {
            if (list?.length == 10) {
                list = [data, ...list.slice(0, -1)];
            } else {
                list = [data, ...list];
            }
        }

        try {
            const res = await callApi(`/user/save_last_visits`, {
                method: "POST",
                headers: {
                    Authorization: state.app.user.token,
                },
                body: JSON.stringify({
                    items: {
                        user_id: state.app.user.user_id,
                        sys_company_id: state.app.user.sys_company_id,
                        last_visits: list,
                    },
                }),
            });

            dispatch({
                type: "SET_LAST_VISIT_LIST",
                payload: list,
            });
        } catch (e) {
            console.log(e);
        }
    };
};

export const logout = (user_id) => {
    return async (dispatch, getState) => {
        try {
            await callApi(`/app/logout`, {
                method: "POST",
                body: JSON.stringify({ id: user_id }),
            });

            localStorage.removeItem("@token");
            localStorage.removeItem("@employee_code");
            localStorage.removeItem("chatWidgetVisible");
            // Clear impersonation flags
            localStorage.removeItem("@impersonated");
            localStorage.removeItem("@roleImpersonated");
            localStorage.removeItem("@impersonate_role");
            if (localStorage.getItem("@impersonated")) {
                window.location.replace("/login");
            }
        } catch (e) {
            // remove error
        } finally {
            clearAllTabScopedDrafts();
            dispatch(resetSocketHandlers())
            dispatch({ type: "LOGOUT" });
        }
    };
};

export const updatePassword = (item) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const res = await callApi(`/user/change_password`, {
                method: "POST",
                body: JSON.stringify(item),
                headers: {
                    Authorization: state.app?.user?.token,
                },
                successMessage: "Se actualizó correctamente",
            });

            dispatch({
                type: "UPDATE_PASSWORD",
                payload: res.body,
            });
        } catch (error) {
            console.log(error.message.error);
        }
    };
};

export const wialonLogin = (item) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const res = await callApi(`/wialon/login`, {
                method: "POST",
                body: JSON.stringify(item),
                headers: {
                    Authorization: state.app?.user?.token,
                },
            });

            dispatch({
                type: "LOGIN_WIALON",
                payload: res.body,
            });
        } catch (error) {
            console.log(error.message.error);
        }
    };
};

export const WialonLoginFromSuperAdmin = (item) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            dispatch({
                type: "LOADING_APP",
                payload: true,
            });
            const res = await callApi(`/wialon/login_from_super_admin`, {
                method: "POST",
                body: JSON.stringify(item),
                headers: {
                    token: state.app?.superAdmin?.token,
                },
            });
            if (res.status == 200) {
                dispatch({
                    type: "LOGIN_WIALON",
                    payload: res.body,
                });

                if (res.body.au && item.sysCompanyId) {
                    const arr = [...state.app?.superAdmin?.sysCompanies];

                    const index = arr.findIndex((s) => s.id == item.sysCompanyId);

                    arr[index] = {
                        ...arr[index],
                        wialon_user_name: res.body.au,
                    };

                    dispatch({
                        type: "UPDATE_SYS_COMPANY_LIST",
                        payload: arr,
                    });
                }
            }
        } catch (error) {
            dispatch({
                type: "ERROR_LOGIN_WIALON",
            });
            console.log(error.message.error);
        }
    };
};

export const CeibaLoginFromSuperAdmin = (ceibaCredentials) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            dispatch({
                type: "LOADING_APP",
                payload: true,
            });
            const res = await callApi(`/ceiba/login_from_super_admin`, {
                method: "POST",
                body: JSON.stringify({ ceibaCredentials }),
                headers: {
                    token: state.app?.superAdmin?.token,
                },
            });
            if (res.status == 200) {
                dispatch({
                    type: "LOGIN_CEIBA",
                    payload: res.body,
                });

                return res.body;
            }
        } catch (error) {
            dispatch({
                type: "ERROR_LOGIN_CEIBA",
            });
            console.log(error.message.error);
        }
    };
};

export const authorizeChangeWialonUser = (credentials) => {
    return async (dispatch, getState) => {
        try {
            const state = getState();
            const res = await callApi(`/wialon/authorize_change_user`, {
                method: "POST",
                body: JSON.stringify(credentials),
                headers: {
                    Authorization: state.app?.user?.token,
                },
            });

            dispatch({
                type: "CHANGE_USER_WIALON",
                payload: res.body,
            });

            return res.body;
        } catch (error) {
            console.log(error.message.error);
        }
    };
};

export const getTimeZonesList = () => {
    return async (dispatch, getState) => {
        const state = getState();
        const res = await callApi(`/utils/time_zones_list`, {
            method: "GET",
            headers: {
                Authorization: state.app?.superAdmin?.token,
            },
        });

        dispatch({
            type: `GET_TIME_ZONES_LIST`,
            payload: res.body,
        });

        return res.body;
    };
};

export const clientLogin = (params) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/client`, {
                method: "POST",
                body: JSON.stringify({
                    ...params,
                }),
            });

            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@client_token", token);

                    dispatch({
                        type: "LOGIN_CLIENT",
                        payload: res.body,
                    });

                    // [LAST VISITS FETCH FIXED - WEB]
                    // Removed await to prevent login loop/blocking
                    const clientData = res.body.client || {};
                    const fetchVisits = async () => {
                        try {
                            const res_visits = await callApi(`/client/last_visits`, {
                                method: "GET",
                                headers: { Authorization: token },
                                params: {
                                    client_id: clientData.client_id || clientData.id,
                                    sys_company_id: clientData.sys_company_id,
                                },
                            });

                            if (res_visits.body.response && res_visits.body.response.length > 0) {
                                dispatch({
                                    type: "SET_CLIENT_LAST_VISIT_LIST",
                                    payload: res_visits.body.response,
                                });
                            } else {
                                dispatch({ type: "INIT_CLIENT_LAST_VISITS_LIST" });
                            }
                        } catch (visitError) {
                            console.error("[clientLogin] Error fetching visits:", visitError);
                        }
                    };
                    fetchVisits();

                    return res.body;
                } catch (e) {
                    throw e;
                }
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión como cliente";
                throw new Error(errorMsg);
            }
        } catch (error) {
            throw error;
        }
    };
};

export const clientLoginWithToken = (options) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/client/token`, {
                method: "POST",
                body: JSON.stringify({
                    token: options.client_token,
                }),
                maxRetries: 2,
                retryDelay: 2000,
            });
            if (res.status === 200) {
                const token = res.body.token;
                setUniqueSessionToken("@client_token", token, options?.nonUnique);
                dispatch({
                    type: "LOGIN_CLIENT",
                    payload: res.body,
                });
                console.log("[clientLoginWithToken] Client Login Response:", res.body);
                const clientData = res.body.client || {};
                console.log("[clientLoginWithToken] Fetching last visits (WEB) for client:", clientData.client_id, "sys_company:", clientData.sys_company_id);

                // Non-fatal: last_visits fetch should not break login
                try {
                    const res_visits = await callApi(`/client/last_visits`, {
                        method: "GET",
                        headers: {
                            Authorization: res.body.token,
                        },
                        params: {
                            client_id: clientData.client_id || clientData.id,
                            sys_company_id: clientData.sys_company_id,
                        },
                    });

                    console.log("[clientLoginWithToken] Last Visits Response:", res_visits);

                    if (res_visits.body.response.length > 0) {
                        dispatch({
                            type: "SET_CLIENT_LAST_VISIT_LIST",
                            payload: res_visits.body.response,
                        });
                    } else {
                        dispatch({
                            type: "INIT_CLIENT_LAST_VISITS_LIST",
                        });
                    }
                } catch (e) {
                    console.error("[clientLoginWithToken] Last visits fetch failed (non-fatal):", e);
                    dispatch({
                        type: "INIT_CLIENT_LAST_VISITS_LIST",
                    });
                }
                return res.body;
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión con token de cliente";
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[clientLoginWithToken] Error:", error);
            throw error;
        }
    };
};

export const clientLogout = (client_id) => {
    return async (dispatch, getState) => {
        try {
            await callApi(`/app/logout/client`, {
                method: "POST",
                body: JSON.stringify({ id: client_id }),
            });

            localStorage.removeItem("@client_token");
            localStorage.removeItem("chatWidgetVisible");
            // Clear impersonation flags from both storages
            localStorage.removeItem("@impersonated");
            localStorage.removeItem("@roleImpersonated");
            localStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("@client_token");
            sessionStorage.removeItem("@impersonated");
            sessionStorage.removeItem("@roleImpersonated");
            sessionStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("active_role");
        } catch (e) {
            // remove error
        } finally {
            clearAllTabScopedDrafts();
            dispatch(resetSocketHandlers())
            dispatch({ type: "CLIENT_LOGOUT" });
        }
    };
};

export const driverLogin = ({ user, password }) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/driver`, {
                method: "POST",
                body: JSON.stringify({
                    user,
                    password,
                }),
            });

            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@driver_token", token);

                    dispatch({
                        type: "LOGIN_DRIVER",
                        payload: res.body,
                    });
                    return res.body;
                } catch (e) {
                    throw e;
                }
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión como operador";
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[driverLogin] Error:", error);
            throw error;
        }
    };
};

export const passengerLogin = ({ user, password }) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/passenger`, {
                method: "POST",
                body: JSON.stringify({
                    user,
                    password,
                }),
            });

            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@passenger_token", token);

                    dispatch({
                        type: "LOGIN_PASSENGER",
                        payload: res.body,
                    });
                    return res.body;
                } catch (e) {
                    throw e;
                }
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión como pasajero";
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[passengerLogin] Error:", error);
            throw error;
        }
    };
};

export const passengerLoginWithToken = (options) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/passenger/token`, {
                method: "POST",
                body: JSON.stringify({
                    token: options.passenger_token,
                }),
                maxRetries: 2,
                retryDelay: 2000,
            });
            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@passenger_token", token, options?.nonUnique);
                    dispatch({
                        type: "LOGIN_PASSENGER",
                        payload: res.body,
                    });
                    return res.body;
                } catch (e) {
                    throw e;
                }
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión con token de pasajero";
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[passengerLoginWithToken] Error:", error);
            throw error;
        }
    };
};

export const driverLoginWithToken = (options) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/driver/token`, {
                method: "POST",
                body: JSON.stringify({
                    token: options.driver_token,
                }),
                maxRetries: 2,
                retryDelay: 2000,
            });
            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@driver_token", token, options?.nonUnique);
                    dispatch({
                        type: "LOGIN_DRIVER",
                        payload: res.body,
                    });
                    return res.body;
                } catch (e) {
                    throw e;
                }
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión con token de operador";
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[driverLoginWithToken] Error:", error);
            throw error;
        }
    };
};

export const driverLogout = (driver_id) => {
    return async (dispatch, getState) => {
        try {
            await callApi(`/app/logout/driver`, {
                method: "POST",
                body: JSON.stringify({ id: driver_id }),
            });

            localStorage.removeItem("@driver_token");
            localStorage.removeItem("chatWidgetVisible");
            // Clear impersonation flags from both storages
            localStorage.removeItem("@impersonated");
            localStorage.removeItem("@roleImpersonated");
            localStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("@driver_token");
            sessionStorage.removeItem("@impersonated");
            sessionStorage.removeItem("@roleImpersonated");
            sessionStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("active_role");
        } catch (e) {
            // remove error
        } finally {
            clearAllTabScopedDrafts();
            dispatch({ type: "DRIVER_LOGOUT" });
        }
    };
};

export const passengerLogout = (passenger_id) => {
    return async (dispatch, getState) => {
        try {
            await callApi(`/app/logout/passenger`, {
                method: "POST",
                body: JSON.stringify({ id: passenger_id }),
            });

            localStorage.removeItem("@passenger_token");
            localStorage.removeItem("chatWidgetVisible");
            // Clear impersonation flags from both storages
            localStorage.removeItem("@impersonated");
            localStorage.removeItem("@roleImpersonated");
            localStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("@passenger_token");
            sessionStorage.removeItem("@impersonated");
            sessionStorage.removeItem("@roleImpersonated");
            sessionStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("active_role");
        } catch (e) {
            // remove error
        } finally {
            clearAllTabScopedDrafts();
            dispatch(resetSocketHandlers())
            dispatch({ type: "PASSENGER_LOGOUT" });
        }
    };
};

export const supplierLogin = ({ email, user, password }) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/supplier`, {
                method: "POST",
                body: JSON.stringify({
                    email: email || user,
                    password,
                }),
            });

            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@supplier_token", token);

                    dispatch({
                        type: "LOGIN_SUPPLIER",
                        payload: res.body,
                    });
                    return res.body;
                } catch (e) {
                    throw e;
                }
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión como proveedor";
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[supplierLogin] Error:", error);
            throw error;
        }
    };
};

export const supplierLoginWithToken = (options) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi(`/app/login/supplier/token`, {
                method: "POST",
                body: JSON.stringify({
                    token: options.supplier_token,
                }),
                maxRetries: 2,
                retryDelay: 2000,
            });

            if (res.status === 200) {
                try {
                    const token = res.body.token;
                    setUniqueSessionToken("@supplier_token", token, options?.nonUnique);
                    dispatch({
                        type: "LOGIN_SUPPLIER",
                        payload: res.body,
                    });
                    let state = getState();
                    let list = [];
                    const res_visits = await callApi(`/supplier/last_visits`, {
                        method: "GET",
                        headers: {
                            Authorization: res.body.token,
                        },
                        params: {
                            supplier_id: res.body.supplier.supplier_id,
                            sys_company_id: res.body.supplier.sys_company_id,
                        },
                    });

                    if (res_visits.body.response.length > 0) {
                        dispatch({
                            type: "SET_SUPPLIER_LAST_VISIT_LIST",
                            payload: res_visits.body.response,
                        });
                    } else {
                        dispatch({
                            type: "INIT_SUPPLIER_LAST_VISITS_LIST",
                        });
                    }
                    return res.body;
                } catch (e) {
                    throw e;
                }
            } else {
                const errorMsg = res.body?.error || "Error al iniciar sesión con token de proveedor";
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("[supplierLoginWithToken] Error:", error);
            throw error;
        }
    };
};

export const supplierLogout = (supplier_id) => {
    return async (dispatch, getState) => {
        try {
            await callApi(`/app/logout/supplier`, {
                method: "POST",
                body: JSON.stringify({ id: supplier_id }),
            });

            localStorage.removeItem("@supplier_token");
            localStorage.removeItem("chatWidgetVisible");
            // Clear impersonation flags from both storages
            localStorage.removeItem("@impersonated");
            localStorage.removeItem("@roleImpersonated");
            localStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("@supplier_token");
            sessionStorage.removeItem("@impersonated");
            sessionStorage.removeItem("@roleImpersonated");
            sessionStorage.removeItem("@impersonate_role");
            sessionStorage.removeItem("active_role");
        } catch (e) {
            // remove error
        } finally {
            clearAllTabScopedDrafts();
            dispatch({ type: "SUPPLIER_LOGOUT" });
        }
    };
};

export const excelLayout = (options) => {
    return async (dispatch, getState) => {
        const state = getState();

        if (options.entity_name === 'cargo_material') {
            options.customRoute = '/travel/generate_cargo_layout';
        }

        // === CORRECCIÓN INTELIGENTE DE RUTA ===
        let endpoint = "";

        // 1. Si customRoute empieza con '/', usamos eso como la ruta completa (sobreescribimos entity_name)
        if (options.customRoute && options.customRoute.startsWith("/")) {
            endpoint = options.customRoute;
        }
        // 2. Si hay customRoute normal, se la pegamos a la entidad
        else if (options.customRoute) {
            endpoint = `/${options.entity_name}/${options.customRoute}`;
        }
        // 3. Si no hay nada, usamos el estándar
        else {
            endpoint = `/${options.entity_name}/generate_layout_excel`;
        }

        const res = await callApi(endpoint, {
            method: "PUT",
            headers: {
                Authorization: state.app.user.token,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(options),
        })
            .then((res) => {
                if (!res.body) {
                    console.error("La respuesta no tiene cuerpo.");
                    return;
                }
                let buff = Buffer.from(res.body);
                return buff;
            })
            .then((blob) => {
                if (!blob) return;
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `${options.entity_name}_layout.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            })
            .catch((error) => {
                console.error("Error en la descarga del Excel:", error);
            });
    };
};

export const importFile = (options) => {
    return async (dispatch, getState) => {
        const state = getState();

        let endpoint = `/${options.entity_name}/import_data_from_excel`;
        if (options.entity_name === 'cargo_material') {
            endpoint = '/travel/import_cargo_excel';
        }

        const res = await callApi(
            endpoint,
            {
                method: "PUT",
                json: false,
                shouldSkipStatusError: true,
                headers: {
                    Authorization: state.app.user.token,
                },
                body: {
                    file: options.files,
                },
            },
        );

        if (res.status !== 200) {
            const body = res.body || {}
            const error = new Error(body.message || 'Tipo de archivo no compatible')
            error.validation_error = body.validation_error || null
            error.details = body.details || null
            throw error
        }

        dispatch({
            type: "IMPORT",
            payload: res.body,
        });

        return res.body;
    };
};

export const verifyImportData = (options) => {
    return async (dispatch, getState) => {
        const state = getState();

        let endpoint = `/${options.entity_name}/validate_data_from_import`;
        let body = JSON.stringify(options.data);

        if (options.entity_name === 'cargo_material') {
            endpoint = '/travel/validate_cargo_import';
            body = JSON.stringify({
                data: options.data,
                travel_id: options.travel_id || 0
            });
        }

        const res = await callApi(
            endpoint,
            {
                method: "PUT",
                headers: {
                    Authorization: state.app.user.token,
                },
                body: body,
            },
        );

        dispatch({
            type: "IMPORT",
            payload: res.body,
        });

        return res.body;
    };
};

export const setLanguage = (lang, setLocalStorage = true) => {
    return async (dispatch, getState) => {
        if (setLocalStorage) {
            await localStorage.setItem("@language", lang);
        }
        dispatch({ type: "SET_LANGUAGE", payload: lang });
    };
};

export const updateAppUser = (partialUser = {}) => {
    return (dispatch) => {
        dispatch({ type: "UPDATE_APP_USER", payload: partialUser });
    };
};

export const associateLoginWithToken = (token) => {
    return async (dispatch, getState) => {
        try {
            const res = await callApi("/app/login/superadmin/token", {
                method: "POST",
                body: JSON.stringify({
                    token,
                }),
            });
            if (res.status == 200) {
                dispatch({ type: "LOGIN_SUPER_ADMIN", payload: res.body });
            }
        } catch (e) {
            console.log("associateLoginWithToken", e.message);
        }
    };
};

export const associateLogout = () => {
    try {
        localStorage.removeItem("@super_admin_token");
        localStorage.removeItem("@token");
        localStorage.removeItem("@employee_code");
        localStorage.removeItem("@supplier_token");
        localStorage.removeItem("@passenger_token");
        localStorage.removeItem("@driver_token");
        localStorage.removeItem("@client_token");
        localStorage.removeItem("@impersonated");
        localStorage.removeItem("@non_unique_session");
        localStorage.removeItem("@roleImpersonated");
    } catch (e) {
        // remove error
    }
    clearAllTabScopedDrafts();
    return {
        type: "LOGOUT",
    };
};

const getSysModules = () => {
    return async (dispatch, getState) => {
        const state = getState();

        try {
            const res = await callApi(`/app/utils/get_sys_modules`, {
                method: "GET",
                headers: {
                    Authorization: state.app.superAdmin.token,
                },
            });

            return res.body;
        } catch (error) {
            return [];
        }
    };
};

const getSysCompanies = () => {
    return async (dispatch, getState) => {
        const state = getState();

        try {
            const res = await callApi(`/app/utils/get_sys_companies`, {
                method: "GET",
                headers: {
                    Authorization: state.app.superAdmin.token,
                },
            });

            return res.body;
        } catch (error) {
            return [];
        }
    };
};

const getSysCompaniesModules = () => {
    return async (dispatch, getState) => {
        const state = getState();

        try {
            const res = await callApi(`/app/utils/get_sys_companies_modules`, {
                method: "GET",
                headers: {
                    Authorization: state.app.superAdmin.token,
                },
            });

            return res.body;
        } catch (error) {
            return [];
        }
    };
};

export default { getSysModules, getSysCompanies, getSysCompaniesModules };
